import { z } from "zod";
import {
  ActionKind,
  ActionStatus,
  AssignmentResult,
  FindingCategory,
  NetworkState,
  ParticipantRole,
  ParticipantStatus,
  SectorStatus,
  TrackingState,
  UnitSettingKey,
  UnitType,
} from "./enums";
import { FindingIcon } from "./finding-icons";
import { BBox, MultiPolygonGeometry, PointGeometry } from "./geo";

// Read models shared by the panel, the phone and the stream. Timestamps are ISO 8601 strings.

const Timestamp = z.iso.datetime({ offset: true });
const Id = z.uuid();

/** Chat channel: whole action, one group, or a direct line to one participant. */
export const Channel = z.union([
  z.literal("action"),
  z.templateLiteral(["group:", z.uuid()]),
  z.templateLiteral(["direct:", z.uuid()]),
]);
export type Channel = z.infer<typeof Channel>;

export const ActionSettings = z.object({
  /** Phones see positions of other groups, not only their own. */
  sharePeersAcrossGroups: z.boolean().default(false),
});
export type ActionSettings = z.infer<typeof ActionSettings>;

export const ActionDto = z.object({
  id: Id,
  organizationId: Id,
  name: z.string(),
  kind: ActionKind,
  status: ActionStatus,
  hqPhone: z.string().nullable(),
  baseLocation: PointGeometry.nullable(),
  areaBbox: BBox.nullable(),
  workingSrid: z.number().int(),
  settings: ActionSettings,
  eventSeq: z.number().int(),
  startedAt: Timestamp.nullable(),
  closedAt: Timestamp.nullable(),
  createdAt: Timestamp,
});
export type ActionDto = z.infer<typeof ActionDto>;

export const UnitSettingDto = z.object({
  unitType: UnitSettingKey,
  sweepWidthM: z.number().positive(),
  maxSpeedMps: z.number().positive(),
});
export type UnitSettingDto = z.infer<typeof UnitSettingDto>;

export const GroupDto = z.object({
  id: Id,
  actionId: Id,
  name: z.string(),
  callsign: z.string().nullable(),
  color: z.string().nullable(),
  leaderParticipantId: Id.nullable(),
  createdAt: Timestamp,
});
export type GroupDto = z.infer<typeof GroupDto>;

export const ParticipantDto = z.object({
  id: Id,
  actionId: Id,
  groupId: Id.nullable(),
  displayName: z.string(),
  phone: z.string().nullable(),
  unitType: UnitType,
  role: ParticipantRole,
  status: ParticipantStatus,
  approved: z.boolean(),
  /** HQ let this responder watch live video (#216); HQ and commanders always can. */
  canViewVideo: z.boolean(),
  joinedAt: Timestamp,
  leftAt: Timestamp.nullable(),
});
export type ParticipantDto = z.infer<typeof ParticipantDto>;

/** Last known state of a participant: what the panel list and map show. */
export const ParticipantStateDto = z.object({
  participantId: Id,
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  accuracyM: z.number().nullable(),
  headingDeg: z.number().nullable(),
  speedMps: z.number().nullable(),
  batteryPct: z.number().int().nullable(),
  network: NetworkState.nullable(),
  status: ParticipantStatus,
  trackingState: TrackingState,
  appVersion: z.string().nullable(),
  platform: z.string().nullable(),
  clockOffsetS: z.number().nullable(),
  pendingEvents: z.number().int().nullable(),
  /** Device time corrected by the measured clock offset. */
  lastPositionAt: Timestamp.nullable(),
  /** When the server last heard from the device (any sync). */
  lastSeenAt: Timestamp.nullable(),
  liveMode: z.boolean(),
});
export type ParticipantStateDto = z.infer<typeof ParticipantStateDto>;

export const SectorDto = z.object({
  id: Id,
  actionId: Id,
  name: z.string(),
  geometry: MultiPolygonGeometry,
  navPoint: PointGeometry,
  areaHa: z.number(),
  status: SectorStatus,
  priority: z.number().int(),
  notes: z.string().nullable(),
  createdAt: Timestamp,
});
export type SectorDto = z.infer<typeof SectorDto>;

export const AssignmentDto = z.object({
  id: Id,
  sectorId: Id,
  groupId: Id,
  assignedAt: Timestamp,
  startedAt: Timestamp.nullable(),
  completedAt: Timestamp.nullable(),
  result: AssignmentResult.nullable(),
});
export type AssignmentDto = z.infer<typeof AssignmentDto>;

export const CoverageDto = z.object({
  sectorId: Id,
  coveredPct: z.number(),
  computedAt: Timestamp,
});
export type CoverageDto = z.infer<typeof CoverageDto>;

export const MessageSender = z.discriminatedUnion("type", [
  z.object({ type: z.literal("participant"), id: Id, name: z.string() }),
  z.object({ type: z.literal("staff"), id: Id, name: z.string() }),
]);
export type MessageSender = z.infer<typeof MessageSender>;

/**
 * Photo of a chat message or a finding (issue #91), taken in the app's camera; its place is the
 * message's or the finding's position. The message or finding arrives first; `ready` turns true
 * when the file is uploaded (message.photo_ready, finding.photo_ready).
 */
export const PhotoDto = z.object({
  id: Id,
  ready: z.boolean(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
});
export type PhotoDto = z.infer<typeof PhotoDto>;

export const MessageDto = z.object({
  id: Id,
  actionId: Id,
  channel: Channel,
  sender: MessageSender,
  body: z.string(),
  location: PointGeometry.nullable(),
  headingDeg: z.number().nullable(),
  createdAt: Timestamp,
  /** seq of the message.sent event: stable order and the cursor for older pages (beforeSeq). */
  eventSeq: z.number().int(),
  /** Absent in messages stored before photos existed (2026-10-10). */
  photo: PhotoDto.nullish(),
});
export type MessageDto = z.infer<typeof MessageDto>;

export const FindingDto = z.object({
  id: Id,
  actionId: Id,
  /** The phone's own id of a reported finding: how it finds HQ's corrections to it (#210). */
  clientId: Id.nullish(),
  reporter: MessageSender,
  location: PointGeometry,
  category: FindingCategory,
  /** Picture on the maps (issue #160); null for findings from before icons: findingIconOf(). */
  icon: FindingIcon.nullish(),
  description: z.string().nullable(),
  foundAt: Timestamp,
  reportedAt: Timestamp,
  /** Absent in findings stored before photos existed (2026-10-10). */
  photo: PhotoDto.nullish(),
});
export type FindingDto = z.infer<typeof FindingDto>;

export const SosDto = z.object({
  id: Id,
  actionId: Id,
  participantId: Id,
  location: PointGeometry.nullable(),
  raisedAt: Timestamp,
  acknowledgedAt: Timestamp.nullable(),
  acknowledgedBy: z.string().nullable(),
  cancelledAt: Timestamp.nullable(),
  cancelledBy: z.string().nullable(),
  /**
   * The phone's `sos` event id: the phone ties HQ's answers to the right SOS by it, not by time
   * (a second SOS a minute after a cancelled one, field test 2026-10-10). Absent before that day.
   */
  clientId: Id.nullish(),
});
export type SosDto = z.infer<typeof SosDto>;

export const RecallDto = z.object({
  id: Id,
  actionId: Id,
  /** null = everybody in the action */
  groupId: Id.nullable(),
  reason: z.string().nullable(),
  issuedAt: Timestamp,
  issuedBy: z.string(),
  confirmations: z.array(z.object({ participantId: Id, confirmedAt: Timestamp })),
});
export type RecallDto = z.infer<typeof RecallDto>;

/** Missing person card as seen by phones: never contains medical notes. */
export const SubjectPublicDto = z.object({
  actionId: Id,
  displayName: z.string(),
  age: z.number().int().nullable(),
  description: z.string().nullable(),
  clothing: z.string().nullable(),
  ipp: PointGeometry.nullable(),
  ippAt: Timestamp.nullable(),
  lkp: PointGeometry.nullable(),
  lkpAt: Timestamp.nullable(),
  updatedAt: Timestamp,
});
export type SubjectPublicDto = z.infer<typeof SubjectPublicDto>;

/** Missing person card for staff: medical notes only for commander and admin. */
export const SubjectDto = SubjectPublicDto.extend({
  medicalNotes: z.string().nullable(),
});
export type SubjectDto = z.infer<typeof SubjectDto>;

export const InviteDto = z.object({
  id: Id,
  actionId: Id,
  label: z.string().nullable(),
  role: ParticipantRole,
  groupId: Id.nullable(),
  expiresAt: Timestamp,
  maxUses: z.number().int().nullable(),
  usedCount: z.number().int(),
  requiresApproval: z.boolean(),
  revokedAt: Timestamp.nullable(),
  createdAt: Timestamp,
});
export type InviteDto = z.infer<typeof InviteDto>;

export const QuarantinedEventDto = z.object({
  id: Id,
  sessionId: Id.nullable(),
  participantId: Id.nullable(),
  clientId: z.string().nullable(),
  reason: z.string(),
  raw: z.unknown(),
  receivedAt: Timestamp,
});
export type QuarantinedEventDto = z.infer<typeof QuarantinedEventDto>;
